"use client";

/**
 * Hook for current user's team memberships.
 * Fetches from GET /api/teams/my-memberships.
 * Provides helpers for checking roles on specific teams.
 */

import { useState, useEffect, useCallback } from "react";

interface TeamMembership {
  teamId: string;
  teamName: string;
  role: "manager" | "approver" | "member";
}

export function useTeamMemberships() {
  const [memberships, setMemberships] = useState<TeamMembership[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/teams/my-memberships");
      if (!res.ok) return;
      const data = await res.json();
      setMemberships(data.memberships || []);
    } catch {
      // Silently fail — non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getMembership = useCallback(
    (teamId: string) => memberships.find((m) => m.teamId === teamId),
    [memberships]
  );

  const isApproverOf = useCallback(
    (teamId: string) => {
      const m = getMembership(teamId);
      return m?.role === "approver" || m?.role === "manager";
    },
    [getMembership]
  );

  const isManagerOf = useCallback(
    (teamId: string) => {
      const m = getMembership(teamId);
      return m?.role === "manager";
    },
    [getMembership]
  );

  return { memberships, loading, refresh, isApproverOf, isManagerOf };
}
